// ScanQrSheet — lector de la tarjeta QR del cliente con la cámara del celular (23-oct-2026).
// Pantalla completa (portal). Usa BarcodeDetector cuando el navegador lo trae (Chrome/Android) y
// jsQR sobre un canvas cuando no (Safari/iOS). Siempre ofrece escribir el código a mano por si la
// cámara no lee (reflejo, pantalla rota) o el permiso está bloqueado.
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Keyboard, Loader2, AlertTriangle } from 'lucide-react';
import jsQR from 'jsqr';
import { parseCardCode } from '../../api/cardScanApi';

interface Props {
  /** Se llama UNA vez con el código ya normalizado (mayúsculas, sin URL). El padre cierra la hoja. */
  onCode: (code: string) => void;
  onClose: () => void;
  /** Texto bajo el visor (p. ej. «Pide a tu cliente que abra su tarjeta»). */
  hint?: string;
}

type Estado = 'iniciando' | 'leyendo' | 'sin-camara' | 'sin-permiso';

// El tipo no existe en todos los lib.dom: se declara lo mínimo que usamos.
type Detector = { detect: (src: HTMLVideoElement) => Promise<{ rawValue: string }[]> };
declare global { interface Window { BarcodeDetector?: new (o?: { formats: string[] }) => Detector } }

export default function ScanQrSheet({ onCode, onClose, hint }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const doneRef = useRef(false);
  const [estado, setEstado] = useState<Estado>('iniciando');
  const [aviso, setAviso] = useState('');
  const [manual, setManual] = useState('');
  const [modoManual, setModoManual] = useState(false);

  const entregar = (raw: string) => {
    if (doneRef.current) return;
    const code = parseCardCode(raw);
    if (!code) {
      // Leyó un QR que no es una tarjeta (menú, Instagram…): se avisa y se sigue leyendo.
      setAviso('Ese QR no es una tarjeta de Lealify. Pide a tu cliente que abra su tarjeta.');
      window.setTimeout(() => setAviso(''), 2500);
      return;
    }
    doneRef.current = true;
    if (navigator.vibrate) navigator.vibrate(60);
    onCode(code);
  };

  useEffect(() => {
    let vivo = true;
    let timer = 0;
    const video = videoRef.current;
    if (!video || !navigator.mediaDevices?.getUserMedia) { setEstado('sin-camara'); setModoManual(true); return; }

    navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      .then(async (stream) => {
        if (!vivo) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');   // iOS: sin esto abre el reproductor a pantalla completa
        await video.play().catch(() => { /* autoplay bloqueado: el usuario toca y arranca */ });
        setEstado('leyendo');

        const detector = window.BarcodeDetector ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;

        const tick = async () => {
          if (!vivo || doneRef.current) return;
          try {
            if (video.readyState >= 2) {
              if (detector) {
                const codes = await detector.detect(video);
                if (codes.length) { entregar(codes[0].rawValue); return; }
              } else {
                // jsQR: se lee a resolución reducida para que no se caliente el teléfono.
                const w = 480, h = Math.round(480 * (video.videoHeight / Math.max(1, video.videoWidth))) || 360;
                canvas.width = w; canvas.height = h;
                ctx.drawImage(video, 0, 0, w, h);
                const img = ctx.getImageData(0, 0, w, h);
                const r = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
                if (r?.data) { entregar(r.data); return; }
              }
            }
          } catch { /* un cuadro malo no debe tirar el lector */ }
          timer = window.setTimeout(tick, detector ? 150 : 220);
        };
        tick();
      })
      .catch((e: DOMException) => {
        if (!vivo) return;
        setEstado(e && (e.name === 'NotAllowedError' || e.name === 'SecurityError') ? 'sin-permiso' : 'sin-camara');
        setModoManual(true);
      });

    return () => {
      vivo = false;
      window.clearTimeout(timer);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enviarManual = (e: React.FormEvent) => {
    e.preventDefault();
    const code = parseCardCode(manual);
    if (!code) { setAviso('El código tiene entre 6 y 12 letras y números (sin O, 0, I ni 1).'); return; }
    doneRef.current = true;
    onCode(code);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black text-white flex flex-col" role="dialog" aria-label="Escanear tarjeta">
      {/* Visor */}
      <div className="relative flex-1 overflow-hidden">
        <video ref={videoRef} muted playsInline className="absolute inset-0 h-full w-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />

        {/* Marco de enfoque */}
        {estado === 'leyendo' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-[68vw] max-w-[320px] aspect-square">
              {(['top-0 left-0 border-t-4 border-l-4 rounded-tl-2xl', 'top-0 right-0 border-t-4 border-r-4 rounded-tr-2xl',
                 'bottom-0 left-0 border-b-4 border-l-4 rounded-bl-2xl', 'bottom-0 right-0 border-b-4 border-r-4 rounded-br-2xl'] as const).map((c) => (
                <span key={c} className={`absolute h-10 w-10 border-white/90 ${c}`} />
              ))}
              <span className="absolute left-3 right-3 top-1/2 h-0.5 bg-[#4ade80]/80 animate-pulse" />
            </div>
          </div>
        )}

        {estado === 'iniciando' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80">
            <Loader2 className="h-8 w-8 animate-spin" /> <span className="text-sm">Abriendo la cámara…</span>
          </div>
        )}

        {(estado === 'sin-permiso' || estado === 'sin-camara') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
            <Camera className="h-10 w-10 text-white/60" />
            <p className="text-base font-semibold">
              {estado === 'sin-permiso' ? 'No tengo permiso para usar la cámara' : 'Este equipo no tiene cámara disponible'}
            </p>
            <p className="text-sm text-white/60">
              {estado === 'sin-permiso'
                ? 'Permite la cámara para este sitio en los ajustes de tu navegador, o escribe el código de la tarjeta abajo.'
                : 'Abre el manager desde tu celular, o escribe el código de la tarjeta abajo.'}
            </p>
          </div>
        )}

        {/* Cerrar */}
        <button type="button" onClick={onClose} aria-label="Cerrar"
          className="absolute top-4 right-4 h-11 w-11 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
          <X className="h-6 w-6" />
        </button>

        {/* Aviso flotante */}
        {aviso && (
          <div className="absolute left-4 right-4 top-20 rounded-xl bg-amber-500/95 text-black text-sm font-medium px-4 py-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> <span>{aviso}</span>
          </div>
        )}
      </div>

      {/* Pie */}
      <div className="bg-black px-5 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-3">
        {estado === 'leyendo' && !modoManual && (
          <p className="text-center text-sm text-white/70">{hint || 'Apunta al QR de la tarjeta de tu cliente'}</p>
        )}
        {modoManual ? (
          <form onSubmit={enviarManual} className="flex gap-2">
            <input
              autoFocus
              value={manual}
              onChange={(e) => { setManual(e.target.value.toUpperCase()); setAviso(''); }}
              placeholder="Código de la tarjeta"
              autoCapitalize="characters" autoCorrect="off" spellCheck={false}
              className="flex-1 rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-base tracking-[0.2em] font-mono uppercase placeholder:tracking-normal placeholder:font-sans placeholder:text-white/40 focus:outline-none focus:border-white/60"
            />
            <button type="submit" className="rounded-xl bg-white text-black font-bold px-5">Buscar</button>
          </form>
        ) : (
          <button type="button" onClick={() => setModoManual(true)}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-white/25 py-3 text-sm font-medium text-white/85">
            <Keyboard className="h-4 w-4" /> ¿No lee? Escribe el código
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
